import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine } from 'recharts';
import { format, parseISO } from 'date-fns';
import { FormControl, Select, MenuItem, Button, Switch, FormControlLabel } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ProductSelector from './ProductSelector';
import '../styles/PostAnalytics.css';
import { apiPost } from '../services/apiClient';
import { formatCompactNumber, formatFullNumber } from '../utils/formatNumber';
import { useStockSeries } from '../hooks/useStockSeries';
import { useStockPremium } from '../hooks/useStockPremium';
import {
    mergeStockIntoRows, mergePremiumIntoRows, benchmarkLabel, periodForMonths,
    STOCK_COLOR, STOCK_DASH_STRING, PREMIUM_MODES,
    formatStockPrice, formatPremiumPct,
} from '../utils/stockOverlay';
import { bucketRowsByPeriod, addRollingAverage, fillBucketGaps } from '../utils/timeSeriesBucketing';

// NEW, separate chart backed by real per-topic engagement (topic_metrics_daily
// via /topic/engagement/daily) -- built ALONGSIDE the original PostLikesAnalytics
// (unchanged, still on the old CRPA-based /topic/posts/get-daily-likes) so the
// two can be compared side by side. See .claude/plans/imperative-splashing-teapot.md.

const ROLLING_AVG_OPTIONS = [
    { value: 5, label: '5 Days' },
    { value: 14, label: '14 Days' },
    { value: 30, label: '30 Days' },
    { value: 60, label: '60 Days' },
    { value: 90, label: '90 Days' },
    { value: 180, label: '180 Days' },
    { value: 365, label: '365 Days' }
];

const BUCKET_PERIOD_OPTIONS = [
    { value: 'day', label: 'Daily' },
    { value: 'week', label: 'Weekly' },
    { value: 'month', label: 'Monthly' }
];

// Per-source colors for the by-source stacked segments. Matched by prefix
// against the raw `source` value (e.g. "youtube_video", "facebook_query")
// since topic_metrics_by_source_daily doesn't always use the bare platform
// name. Falls through the fixed palette below for anything unrecognized.
const SOURCE_COLOR_MAP = [
    { prefix: 'reddit', color: '#FF5700' },
    { prefix: 'youtube', color: '#FF0000' },
    { prefix: 'facebook', color: '#1877F2' },
    { prefix: 'instagram', color: '#F0578A' },
    { prefix: 'linkedin', color: '#2E7D32' },
    { prefix: 'tiktok', color: '#757575' },
];
const FALLBACK_SOURCE_COLORS = [
    '#8884d8', '#ffc658', '#4fc3f7', '#ba68c8', '#ffa726', '#26a69a', '#9ccc65'
];
function getSourceColor(sourceValue, fallbackIndex) {
    const normalized = (sourceValue || '').toLowerCase();
    const match = SOURCE_COLOR_MAP.find(({ prefix }) => normalized.startsWith(prefix));
    if (match) return match.color;
    return FALLBACK_SOURCE_COLORS[fallbackIndex % FALLBACK_SOURCE_COLORS.length];
}

const PostEngagementAnalytics = ({ topicMap, companyName, companyTicker, autoSearch = false,
    initialStartDate, initialEndDate, initialBucket,
    // Metric to chart from /topic/engagement/daily. Defaults preserve the
    // original "Post Engagement" (likes) behavior; pass metric="views" (etc.)
    // for the Post Views tab.
    metric = 'likes', chartTitle = 'Post Engagement (New)',
    valueAxisLabel = 'Number of Likes', barName = 'Like Count', note = '',
    // When set (e.g. "youtube"), restrict the chart to a SINGLE source: pulls
    // topic_metrics_by_source_daily and keeps only rows whose source starts with
    // this value. Turns the tracker into a one-platform view (prefix match, so
    // 'youtube' matches 'youtube'/'youtube_video'). Undefined = all sources.
    sourceFilter,
    // Embedded mode: hide this chart's own control bar and drive it from a shared
    // parent control bar. startDate/endDate/bucketPeriod become controlled props
    // and the chart auto-fetches on any change (no per-chart Search button).
    embedded = false,
    startDate: controlledStartDate, endDate: controlledEndDate,
    bucketPeriod: controlledBucketPeriod,
    // Zero-fill empty buckets across the visible range so every bucket gets a
    // bar (opt-in; the sparse by-source series would otherwise show a few fat
    // bars smeared across the axis). chartHeight shrinks the plot so several
    // charts stack into view at once.
    fillEmptyBuckets = false, chartHeight = 500,
    // Optional controlled product selection, so a parent (e.g. the Daily
    // Trackers tab switcher) can keep the selected topic sticky across
    // different chart types. Falls back to internal state when omitted.
    // Compact the value-axis ticks (1000000 -> "1M") + full number in tooltip.
    // Opt-in so other usages are unchanged; matches PostSentimentEngagementAnalytics.
    compactYAxis = false,
    selectedProduct: controlledSelectedProduct, onProductChange }) => {
    const [data, setData] = useState([]);
    // Coverage of the currently-selected topic in topic_metrics_daily. null =
    // not yet searched; { has_data: false } means the topic isn't tracked yet
    // (never refreshed by the recheck pipeline) -- distinct from "tracked but
    // zero engagement" (has_data: true, all zeros).
    const [coverage, setCoverage] = useState(null);
    // Overlay mode: 'off' | 'price' | 'peers' | 'snp500' | 'sector'.
    const [overlayMode, setOverlayMode] = useState('off');
    const [internalSelectedProduct, setInternalSelectedProduct] = useState('');
    const isProductControlled = controlledSelectedProduct !== undefined && typeof onProductChange === 'function';
    const selectedProduct = isProductControlled ? controlledSelectedProduct : internalSelectedProduct;
    const setSelectedProduct = isProductControlled ? onProductChange : setInternalSelectedProduct;
    const [rollingDays, setRollingDays] = useState(90);
    const [internalBucketPeriod, setBucketPeriod] = useState(initialBucket || 'month');
    const bucketPeriod = controlledBucketPeriod !== undefined ? controlledBucketPeriod : internalBucketPeriod;
    const [isLoading, setIsLoading] = useState(false);
    // "By Source" toggle: when on, each bar is stacked into segments by
    // post source instead of one solid bar. Not supported for "All Topics".
    // Reads topic_metrics_by_source_daily -- the same amortized grain as
    // topic_metrics_daily, split by source -- so segments reconcile with
    // the total bar (verified to within integer-split rounding).
    const [breakdownBySource, setBreakdownBySource] = useState(false);
    // Distinct sources actually present in the last by-source fetch (drives
    // which stacked <Bar> segments/legend/colors get rendered) -- derived
    // from the response itself so it's never out of sync with what was
    // actually returned.
    const [breakdownSources, setBreakdownSources] = useState([]);

    const today = new Date();

    const [internalStartDate, setStartDate] = useState(initialStartDate || '2024-01-01');
    const startDate = controlledStartDate !== undefined ? controlledStartDate : internalStartDate;
    const [internalEndDate, setEndDate] = useState(initialEndDate || today.toISOString().split('T')[0]);
    const endDate = controlledEndDate !== undefined ? controlledEndDate : internalEndDate;
    const [isHeaderHovered, setIsHeaderHovered] = useState(false);

    const filteredTopicMap = useMemo(() => {
        if (!topicMap) return {};

        if (Object.keys(topicMap).length > 0 && !selectedProduct) {
            const sortedTopics = Object.values(topicMap).sort((a, b) => {
                const priorityA = a.priority === undefined ? Infinity : a.priority;
                const priorityB = b.priority === undefined ? Infinity : b.priority;
                return priorityA - priorityB;
            });

            if (sortedTopics[0]) {
                setSelectedProduct(sortedTopics[0].id);
            }
        }

        return {
            ...topicMap,
            'all': {
                id: 'all',
                name: 'All Topics',
                priority: 9999
            }
        };
    }, [topicMap, selectedProduct]);

    const getSelectedProductName = () => {
        if (selectedProduct && filteredTopicMap && filteredTopicMap[selectedProduct]) {
            return filteredTopicMap[selectedProduct].name;
        }
        return 'Product';
    };

    const getBucketLabel = () => {
        const bucket = BUCKET_PERIOD_OPTIONS.find(b => b.value === bucketPeriod);
        return bucket ? bucket.label : 'Daily';
    };

    const buildMainTitle = () => {
        return `${getSelectedProductName()} · ${chartTitle}`;
    };

    const buildSubline = () => {
        const dateRange = `${format(parseISO(startDate), 'MMM d, yyyy')} - ${format(parseISO(endDate), 'MMM d, yyyy')}`;
        return `${dateRange} • ${getBucketLabel()}`;
    };

    // Tracks whether a search has ever completed, so the "By Source" toggle
    // (below) knows it's safe to auto-refetch rather than firing on mount.
    const hasSearchedOnceRef = useRef(false);

    const handleSearch = useCallback(async () => {
        if (!selectedProduct) return;
        hasSearchedOnceRef.current = true;

        setIsLoading(true);
        setCoverage(null);
        try {
            let rawData = [];
            // Per-day, per-source counts (only fetched/used when the "By
            // Source" toggle is on): { [day]: { [`src_${sourceValue}`]: value } }
            let sourceBreakdownByDay = null;

            if (selectedProduct === 'all') {
                console.log('Fetching engagement data for all topics...');
                setBreakdownSources([]); // not supported for "All Topics"
                const topicIds = Object.keys(topicMap);

                // NOTE: a post tagged to multiple topics contributes to each,
                // so this sum double-counts multi-topic posts. Acceptable for
                // a topic-activity view (matches the old chart's shape); a
                // de-duped server-side "all topics" endpoint is a follow-up.
                const allTopicResults = await Promise.all(
                    topicIds.map(async (topicId) => {
                        try {
                            const response = await apiPost('topic/engagement/daily', {
                                topic_id: topicId,
                                start_date: startDate,
                                end_date: endDate,
                                metric,
                            });

                            if (!response.ok) {
                                throw new Error(`Backend error: ${response.status}`);
                            }

                            const json = await response.json();
                            return { data: json.data ?? [], coverage: json.coverage ?? { has_data: false } };
                        } catch (error) {
                            console.error(`Error fetching engagement data for topic ${topicId}:`, error);
                            return { data: [], coverage: { has_data: false } };
                        }
                    })
                );

                const aggregatedData = {};
                allTopicResults.forEach(({ data: topicData }) => {
                    if (!Array.isArray(topicData)) return;

                    topicData.forEach(item => {
                        const date = item.day;
                        const value = typeof item.value === 'bigint'
                            ? Number(item.value.toString())
                            : Number(item.value);

                        if (!aggregatedData[date]) {
                            aggregatedData[date] = {
                                day: date,
                                num_posts: 0
                            };
                        }

                        aggregatedData[date].num_posts += value;
                    });
                });

                rawData = Object.values(aggregatedData);
                setCoverage({ has_data: allTopicResults.some(r => r.coverage?.has_data) });
                console.log('Aggregated engagement data for all topics:', rawData);

            } else if (sourceFilter) {
                // Single-source view (e.g. YouTube only): pull the by-source rows
                // and keep just this platform, so this is the daily-tracker chart
                // scoped to one source. Prefix match since the source label may
                // be 'youtube' or 'youtube_video'.
                setBreakdownSources([]);
                const sourceResponse = await apiPost('topic/engagement/by-source', {
                    topic_id: selectedProduct,
                    start_date: startDate,
                    end_date: endDate,
                    metric,
                });
                if (!sourceResponse.ok) throw new Error(`Backend error: ${sourceResponse.status}`);
                const sourceJson = await sourceResponse.json();
                const byDay = {};
                (sourceJson.data ?? []).forEach(item => {
                    if (!(item.source || '').toLowerCase().startsWith(sourceFilter)) return;
                    const value = typeof item.value === 'bigint'
                        ? Number(item.value.toString()) : Number(item.value);
                    byDay[item.day] = (byDay[item.day] || 0) + value;
                });
                rawData = Object.entries(byDay).map(([day, value]) => ({ day, num_posts: value }));
                setCoverage({ has_data: rawData.length > 0 });

            } else {
                if (breakdownBySource) {
                    try {
                        const sourceResponse = await apiPost('topic/engagement/by-source', {
                            topic_id: selectedProduct,
                            start_date: startDate,
                            end_date: endDate,
                            metric,
                        });
                        if (!sourceResponse.ok) throw new Error(`Backend error: ${sourceResponse.status}`);
                        const sourceJson = await sourceResponse.json();

                        sourceBreakdownByDay = {};
                        const sourcesSeen = new Set();
                        (sourceJson.data ?? []).forEach(item => {
                            const value = typeof item.value === 'bigint'
                                ? Number(item.value.toString())
                                : Number(item.value);
                            if (!sourceBreakdownByDay[item.day]) sourceBreakdownByDay[item.day] = {};
                            sourceBreakdownByDay[item.day][`src_${item.source}`] = value;
                            sourcesSeen.add(item.source);
                        });
                        setBreakdownSources(
                            Array.from(sourcesSeen).sort().map(value => ({
                                value,
                                label: value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ')
                            }))
                        );
                    } catch (error) {
                        console.error('Error fetching engagement by source:', error);
                        sourceBreakdownByDay = {};
                        setBreakdownSources([]);
                    }
                } else {
                    setBreakdownSources([]);
                }

                const response = await apiPost('topic/engagement/daily', {
                    topic_id: selectedProduct,
                    start_date: startDate,
                    end_date: endDate,
                    metric,
                });

                if (!response.ok) {
                    throw new Error(`Backend error: ${response.status}`);
                }

                const json = await response.json();
                setCoverage(json.coverage ?? { has_data: false });
                // Normalize {day, value} -> {day, num_posts} to match the
                // "All Topics" branch's aggregated shape below.
                rawData = (json.data ?? []).map(item => ({ day: item.day, num_posts: item.value }));
            }

            console.log('Raw engagement data received:', rawData);

            if (!Array.isArray(rawData) || rawData.length === 0) {
                console.log('No engagement data received or data is empty');
                setData([]);
                return;
            }

            // Every source key defaults to 0 so the stacked bar has no gaps
            // on days where a given source had no engagement.
            const zeroedSourceFields = sourceBreakdownByDay
                ? Object.fromEntries(
                    Array.from(new Set(Object.values(sourceBreakdownByDay).flatMap(Object.keys))).map(key => [key, 0])
                )
                : {};

            const processedData = rawData.map(item => {
                const numLikes = typeof item.num_posts === 'bigint'
                    ? Number(item.num_posts.toString())
                    : Number(item.num_posts);

                return {
                    date: format(parseISO(item.day), 'MMM dd'),
                    fullDate: item.day,
                    likeCount: numLikes,
                    ...zeroedSourceFields,
                    ...(sourceBreakdownByDay?.[item.day] || {})
                };
            });

            processedData.sort((a, b) => {
                const dateA = parseISO(a.fullDate);
                const dateB = parseISO(b.fullDate);
                return dateA - dateB;
            });

            let aggregatedData = bucketRowsByPeriod(processedData, bucketPeriod);
            if (fillEmptyBuckets) {
                // Zero-fill empty buckets before the rolling average so it runs
                // across the zeros too. src_* fields default to 0 for empty buckets.
                const zeroFields = { likeCount: 0, ...zeroedSourceFields };
                aggregatedData = fillBucketGaps(aggregatedData, startDate, endDate, bucketPeriod, zeroFields);
            }
            const dataWithRollingAvg = addRollingAverage(aggregatedData, rollingDays, bucketPeriod);

            console.log('Final processed engagement data with rolling average:', dataWithRollingAvg);
            setData(dataWithRollingAvg);
        } catch (error) {
            console.error('Error in fetchData:', error);
            setData([]);
        } finally {
            setIsLoading(false);
        }
    }, [selectedProduct, startDate, endDate, rollingDays, bucketPeriod, topicMap, metric, breakdownBySource, sourceFilter, fillEmptyBuckets]);

    // Capture mode: fire the search once automatically (no manual click) so the
    // chart renders when this component is mounted off-screen for the Report
    // Generator. Has no effect on the live page unless `autoSearch` is set.
    const autoSearchedRef = useRef(false);
    useEffect(() => {
        if (autoSearch && selectedProduct && !autoSearchedRef.current) {
            autoSearchedRef.current = true;
            handleSearch();
        }
    }, [autoSearch, selectedProduct, handleSearch]);

    // Embedded mode: auto-fetch whenever the shared inputs change (no Search click).
    useEffect(() => {
        if (embedded && selectedProduct) handleSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [embedded, selectedProduct, startDate, endDate, bucketPeriod, metric, sourceFilter]);

    // Re-run the search when "By Source" is flipped, once a search has
    // already happened -- otherwise flipping it does nothing until the user
    // separately clicks Search, with no indication that's required.
    useEffect(() => {
        if (hasSearchedOnceRef.current && selectedProduct) {
            handleSearch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [breakdownBySource]);

    // Stock overlay (publicly-traded companies only). 'price' shows the absolute
    // adjusted-close line; 'peers' / 'snp500' / 'sector' show a relative-
    // performance ("premium") comparison indexed to 100 at the window start.
    const priceMode = overlayMode === 'price';
    const premiumMode = ['peers', 'snp500', 'sector'].includes(overlayMode) ? overlayMode : null;

    // Request the premium window that matches the chart's visible date range so a
    // backend (period-start-baselined) premium lines up with what's shown.
    const premiumPeriod = useMemo(
        () => periodForMonths(Math.round((new Date(endDate) - new Date(startDate)) / (30 * 86400000))),
        [startDate, endDate]
    );
    const { series: stockSeries } = useStockSeries(priceMode ? companyTicker : null);
    const { payload: premium, error: premiumError } = useStockPremium(companyTicker, premiumMode, premiumPeriod);

    const showStockOverlay = Boolean(companyTicker) && priceMode;
    const showPremiumOverlay = Boolean(companyTicker) && Boolean(premiumMode) && Boolean(premium?.data?.length);
    const benchLabel = premium ? benchmarkLabel(premium) : '';
    const premiumName = premium ? `${companyTicker} vs ${benchLabel}` : '';

    const chartData = useMemo(() => {
        if (showStockOverlay) return mergeStockIntoRows(data, stockSeries, bucketPeriod);
        if (showPremiumOverlay) return mergePremiumIntoRows(data, premium.data, bucketPeriod);
        return data;
    }, [data, stockSeries, premium, bucketPeriod, showStockOverlay, showPremiumOverlay]);

    const CONTROL_HEIGHT = 36;
    const CONTROL_GAP = 12;

    const selectStyle = {
        width: '100%',
        height: CONTROL_HEIGHT,
        '& .MuiSelect-select': {
            paddingTop: '6px',
            paddingBottom: '6px',
            paddingRight: '24px !important',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d0d0d0',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#999',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1976d2',
        }
    };

    const filterLabelStyle = {
        fontSize: '9px',
        fontWeight: 600,
        color: '#999',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '3px'
    };

    const dateInputStyle = {
        padding: '6px 10px',
        borderRadius: '4px',
        border: '1px solid #d0d0d0',
        fontSize: '13px',
        height: CONTROL_HEIGHT,
        boxSizing: 'border-box',
        transition: 'border-color 0.15s ease',
        outline: 'none',
        width: '100%'
    };

    return (
        <div style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
            {/* Header Card */}
            <div
                onMouseEnter={() => setIsHeaderHovered(true)}
                onMouseLeave={() => setIsHeaderHovered(false)}
                style={{
                    backgroundColor: '#fafafa',
                    borderRadius: '8px',
                    padding: '20px 24px 18px',
                    marginBottom: '20px',
                    boxShadow: isHeaderHovered
                        ? '0 4px 12px rgba(0, 0, 0, 0.08)'
                        : '0 1px 3px rgba(0, 0, 0, 0.06)',
                    border: '1px solid rgba(0, 0, 0, 0.06)',
                    transition: 'box-shadow 0.2s ease',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                }}
            >
                {/* Title Block */}
                <div style={{ marginBottom: '12px' }}>
                    <div style={{
                        fontSize: '11px',
                        color: '#777',
                        fontWeight: 500,
                        lineHeight: 1.2,
                        marginBottom: '1px'
                    }}>
                        {companyName || 'Company'}
                    </div>

                    <h1 style={{
                        fontSize: '22px',
                        fontWeight: 700,
                        margin: 0,
                        color: '#111',
                        lineHeight: 1.25
                    }}>
                        {selectedProduct ? buildMainTitle() : 'Select a Product'}
                    </h1>

                    {selectedProduct && (
                        <div style={{
                            fontSize: '12px',
                            color: '#999',
                            lineHeight: 1.3,
                            marginTop: '2px'
                        }}>
                            {buildSubline()}
                        </div>
                    )}

                    {coverage?.has_data && (
                        <div style={{
                            fontSize: '11px',
                            color: '#b58a00',
                            lineHeight: 1.3,
                            marginTop: '4px'
                        }}>
                            Posts within 90 days may rise as posts continue to be rechecked.
                        </div>
                    )}

                    {note && (
                        <div style={{
                            fontSize: '11px',
                            color: '#777',
                            lineHeight: 1.3,
                            marginTop: '4px',
                            fontStyle: 'italic'
                        }}>
                            {note}
                        </div>
                    )}
                </div>

                {/* Filter Bar (hidden in embedded mode — shared controls live in the parent) */}
                {!embedded && (
                <div style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: `${CONTROL_GAP}px`,
                    flexWrap: 'wrap',
                    rowGap: '12px'
                }}>
                    {/* Product */}
                    <div style={{ flex: '1 1 auto', minWidth: 100, maxWidth: 160, boxSizing: 'border-box' }}>
                        <div style={filterLabelStyle}>Product</div>
                        <ProductSelector
                            topicMap={filteredTopicMap}
                            selectedId={selectedProduct || ''}
                            onSelect={setSelectedProduct}
                            label=""
                            size="small"
                            sx={selectStyle}
                        />
                    </div>

                    {/* Start Date */}
                    <div style={{ flex: '0 0 auto', minWidth: 110 }}>
                        <div style={filterLabelStyle}>Start Date</div>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            max={endDate}
                            style={dateInputStyle}
                        />
                    </div>

                    {/* End Date */}
                    <div style={{ flex: '0 0 auto', minWidth: 110 }}>
                        <div style={filterLabelStyle}>End Date</div>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            min={startDate}
                            max={new Date().toISOString().split('T')[0]}
                            style={dateInputStyle}
                        />
                    </div>

                    {/* Rolling Average */}
                    <div style={{ flex: '1 1 auto', minWidth: 90, maxWidth: 120 }}>
                        <div style={filterLabelStyle}>Rolling Avg</div>
                        <FormControl size="small" sx={selectStyle}>
                            <Select
                                value={rollingDays}
                                onChange={(e) => setRollingDays(Number(e.target.value))}
                            >
                                {ROLLING_AVG_OPTIONS.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    {/* Bucket Period */}
                    <div style={{ flex: '1 1 auto', minWidth: 80, maxWidth: 110 }}>
                        <div style={filterLabelStyle}>Bucket</div>
                        <FormControl size="small" sx={selectStyle}>
                            <Select
                                value={bucketPeriod}
                                onChange={(e) => setBucketPeriod(e.target.value)}
                            >
                                {BUCKET_PERIOD_OPTIONS.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    {/* By Source toggle: stacks each bar into segments by
                        post source instead of one solid bar. Not available
                        for "All Topics" (would require an N-topics fan-out
                        on top of the by-source query). */}
                    {selectedProduct !== 'all' && !sourceFilter && (
                        <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={breakdownBySource}
                                        onChange={(e) => setBreakdownBySource(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={<span style={{ fontSize: '12px', color: '#555' }}>By Source</span>}
                                sx={{ marginLeft: 0 }}
                            />
                        </div>
                    )}

                    {/* Stock overlay (publicly-traded companies only): absolute
                        price, or a relative-performance comparison vs peers /
                        S&P 500 / sector. */}
                    {companyTicker && (
                        <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                            <div style={filterLabelStyle}>Overlay</div>
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                                <Select
                                    value={overlayMode}
                                    onChange={(e) => setOverlayMode(e.target.value)}
                                    sx={{ height: CONTROL_HEIGHT }}
                                >
                                    <MenuItem value="off">Off</MenuItem>
                                    {PREMIUM_MODES.map((m) => (
                                        <MenuItem key={m.value} value={m.value}>
                                            {m.value === 'price' ? `Price (${companyTicker})` : m.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {premiumMode && premiumError && (
                                <div style={{ fontSize: '11px', color: '#b00', marginTop: '2px', maxWidth: 140 }}>
                                    No comparison available
                                </div>
                            )}
                        </div>
                    )}

                    {/* Search Button */}
                    <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                        <Button
                            variant="contained"
                            onClick={handleSearch}
                            disabled={!selectedProduct || isLoading}
                            startIcon={<SearchIcon />}
                            sx={{
                                height: CONTROL_HEIGHT,
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 2.5,
                            }}
                        >
                            {isLoading ? 'Loading...' : 'Search'}
                        </Button>
                    </div>
                </div>
                )}
            </div>

            {/* Chart Section */}
            {coverage && !coverage.has_data ? (
                <div style={{
                    marginTop: '8px',
                    width: '100%',
                    height: '200px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    color: '#777',
                    fontSize: '14px',
                    backgroundColor: '#fafafa',
                    borderRadius: '8px',
                    border: '1px solid rgba(0, 0, 0, 0.06)',
                }}>
                    Engagement not yet tracked for this topic — it hasn't been checked by the
                    recheck pipeline yet.
                </div>
            ) : (
            <div style={{ marginTop: '8px', width: '100%', height: `${chartHeight}px` }}>
                <ResponsiveContainer>
                    <ComposedChart data={chartData}>

                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="date"
                            angle={-45}
                            textAnchor="end"
                            height={60}
                        />
                        <YAxis
                            yAxisId="left"
                            tickFormatter={compactYAxis ? formatCompactNumber : undefined}
                            label={{
                                value: valueAxisLabel,
                                angle: -90,
                                position: 'insideLeft',
                                style: { textAnchor: 'middle' }
                            }}
                        />
                        {showStockOverlay && (
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                domain={['auto', 'auto']}
                                tickFormatter={formatStockPrice}
                                label={{
                                    value: 'Stock Price ($)',
                                    angle: 90,
                                    position: 'insideRight'
                                }}
                            />
                        )}
                        {showPremiumOverlay && (
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                domain={([dataMin, dataMax]) => [Math.min(0, dataMin), Math.max(0, dataMax)]}
                                tickFormatter={(v) => `${Math.round(v)}%`}
                                label={{
                                    value: 'Premium (%)',
                                    angle: 90,
                                    position: 'insideRight'
                                }}
                            />
                        )}
                        <Tooltip formatter={(value, name) => (
                            name === `Stock Price (${companyTicker})`
                                ? formatStockPrice(value)
                                : name === premiumName
                                    ? formatPremiumPct(value)
                                    : (compactYAxis ? formatFullNumber(value) : value)
                        )} />
                        <Legend />
                        {breakdownBySource && breakdownSources.length > 0 ? (
                            breakdownSources.map((source, index) => (
                                <Bar
                                    key={source.value}
                                    yAxisId="left"
                                    dataKey={`src_${source.value}`}
                                    stackId="engagement"
                                    fill={getSourceColor(source.value, index)}
                                    name={source.label}
                                />
                            ))
                        ) : (
                            <Bar
                                yAxisId="left"
                                dataKey="likeCount"
                                fill="#82ca9d"
                                name={barName}
                            />
                        )}
                        <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="rollingAvg"
                            stroke="#000000"
                            strokeWidth={2}
                            name={`${rollingDays}-Day Rolling Average`}
                            dot={{ fill: '#808080', strokeWidth: 1, r: 2 }}
                            activeDot={{ r: 6, stroke: '#ff7300', strokeWidth: 1 }}
                        />
                        {showStockOverlay && (
                            <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey="stockPrice"
                                stroke={STOCK_COLOR}
                                strokeWidth={2}
                                strokeDasharray={STOCK_DASH_STRING}
                                name={`Stock Price (${companyTicker})`}
                                dot={false}
                                connectNulls
                            />
                        )}
                        {showPremiumOverlay && (
                            <ReferenceLine yAxisId="right" y={0} stroke="#bbb" strokeDasharray="3 3" />
                        )}
                        {showPremiumOverlay && (
                            <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey="premiumPct"
                                stroke={STOCK_COLOR}
                                strokeWidth={2}
                                strokeDasharray={STOCK_DASH_STRING}
                                name={premiumName}
                                dot={false}
                                connectNulls
                            />
                        )}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
            )}
        </div>
    );
};

export default PostEngagementAnalytics;
